function isPromiseLike(value) {
    return typeof value === 'object' && value !== null && 'then' in value && typeof value.then === 'function';
}
export class AccessiblePromise {
    #status;
    #value;
    constructor(value, rejected) {
        this.#value = value;
        if (isPromiseLike(value)) {
            // both value and reason can be promise like in which case we are still pending
            this.#status = 'pending';
            value.then(v => {
                this.#status = 'fulfilled';
                this.#value = v;
            }, reason => {
                this.#status = 'rejected';
                this.#value = reason;
            });
        }
        else {
            this.#status = rejected ? 'rejected' : 'fulfilled';
        }
    }
    chain(value, rejected) {
        if (value instanceof AccessiblePromise)
            return value;
        return new AccessiblePromise(value, rejected);
    }
    get status() {
        return this.#status;
    }
    get value() {
        switch (this.#status) {
            case 'fulfilled':
                return this.#value;
            case 'rejected':
                throw this.#value;
            case 'pending':
                throw Object.assign(new Error('Promise is pending.'), { then: this.then.bind(this) });
            default:
                throw new Error('Unreachable');
        }
    }
    get reason() {
        switch (this.#status) {
            case 'fulfilled':
                return undefined;
            case 'rejected':
                throw this.#value;
            case 'pending':
                throw Object.assign(new Error('Promise is pending.'), { then: this.then.bind(this) });
            default:
                throw new Error('Unreachable');
        }
    }
    then(onfulfilled, onrejected) {
        let value = this.#value;
        let rejected = false;
        // eslint-disable-next-line default-case
        switch (this.#status) {
            case 'pending':
                value = value.then(onfulfilled, onrejected);
                break;
            case 'fulfilled':
                if (onfulfilled) {
                    try {
                        value = onfulfilled(value);
                    }
                    catch (e) {
                        value = e;
                        rejected = true;
                    }
                }
                break;
            case 'rejected':
                if (onrejected) {
                    try {
                        value = onrejected(value);
                    }
                    catch (e) {
                        value = e;
                        rejected = true;
                    }
                }
                break;
        }
        return this.chain(value, rejected);
    }
    catch(onrejected) {
        return this.then(undefined, onrejected);
    }
    finally(onfinally) {
        return this.then(value => {
            onfinally?.();
            return value;
        }, reason => {
            onfinally?.();
            throw reason;
        });
    }
    orSupply(supplier) {
        if (this.#status === 'fulfilled') {
            return this.#value;
        }
        if (this.#status === 'rejected') {
            throw this.#value;
        }
        return supplier();
    }
    orSuspend() {
        return this.orSupply(() => {
            const error = new Error('Promise is not fulfilled.');
            const then = this.#value.then.bind(this.#value);
            throw Object.assign(error, { then });
        });
    }
    orThrow() {
        return this.orSupply(() => {
            throw new Error('Promise is not fulfilled.');
        });
    }
    or(value) {
        return this.orSupply(() => value);
    }
    get [Symbol.toStringTag]() {
        return 'AccessiblePromise';
    }
    static resolve(value) {
        if (value instanceof AccessiblePromise)
            return value;
        return new AccessiblePromise(value);
    }
    static reject(reason) {
        return new AccessiblePromise(reason, true);
    }
}
//# sourceMappingURL=AccessiblePromise.js.map